import { FloatingContext, FloatingFocusManager, FloatingPortal } from '@floating-ui/react'
import { AnimatedContentLoader, Button, SearchInput } from '@root/components'
import { FaCheck, FaPen, FaPlus } from 'react-icons/fa'
import styled from 'styled-components'
import { collectFromPages, IContact, useInfiniteContacts } from '@root/query'
import { useVirtualList } from '@root/hooks'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const ITEM_HEIGHT = 38

interface ContactsDropdownProps {
  context: FloatingContext
  refs: FloatingContext['refs']
  getFloatingProps: (userProps?: React.HTMLProps<HTMLElement>) => Record<string, unknown>
  selectedIds: number[]
  onContactSelect: (contactId: number) => void
  onEditContact?: (contact: IContact) => void
  onCreateContact?: () => void
}

const ContactsDropdown: React.FC<ContactsDropdownProps> = ({
  context,
  refs,
  getFloatingProps,
  selectedIds,
  onContactSelect,
  onEditContact,
  onCreateContact
}) => {
  const [focusIndex, setFocusIndex] = useState(-1)
  const [isEditButtonFocused, setIsEditButtonFocused] = useState(false)
  const [search, setSearch] = useState('')
  const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))
  const [t] = useTranslation()

  const { data, isLoading, hasNextPage, fetchNextPage } = useInfiniteContacts({ search })

  const contacts = collectFromPages(data)
  const allItemsLength = contacts?.length || 0

  const onScrollBottom = () => {
    if (hasNextPage && !isLoading) {
      fetchNextPage()
    }
  }

  const { scrollRef, renderItems, scrollToIndex } = useVirtualList({
    items: contacts,
    itemSize: ITEM_HEIGHT,
    onScrollBottom
  })

  const handleContactSelect = (contact: IContact) => {
    onContactSelect?.(contact.id)
  }

  const handleArrowDown = () => {
    if (isEditButtonFocused) return

    if (focusIndex === allItemsLength - 1) {
      setFocusIndex(0)
      scrollToIndex(0)
      return
    }

    const newIndex = Math.min(focusIndex + 1, allItemsLength - 1)
    setFocusIndex(newIndex)
    scrollToIndex(newIndex)
  }

  const handleArrowUp = () => {
    if (isEditButtonFocused) return

    if (focusIndex === 0) {
      setFocusIndex(allItemsLength - 1)
      scrollToIndex(allItemsLength - 1)
      return
    }

    const newIndex = Math.max(focusIndex - 1, 0)
    setFocusIndex(newIndex)
    scrollToIndex(newIndex)
  }

  const handleTab = e => {
    if (!onEditContact) return
    if (focusIndex >= 0 && focusIndex < allItemsLength) {
      e.preventDefault()
      setIsEditButtonFocused(!isEditButtonFocused)
    }
  }

  const handleEnter = e => {
    e.stopPropagation()
    e.preventDefault()

    if (isEditButtonFocused) {
      const contact = contacts[focusIndex]
      if (contact && onEditContact) {
        onEditContact(contact)
      }
    } else {
      const result = contacts[focusIndex]
      if (result) {
        handleContactSelect(result)
      }
    }
  }

  const onKeyDown = e => {
    switch (e.key) {
      case 'ArrowDown':
        handleArrowDown()
        break
      case 'ArrowUp':
        handleArrowUp()
        break
      case 'Tab':
        handleTab(e)
        break
      case 'Enter':
        handleEnter(e)
        break
      default:
        break
    }
  }

  return (
    <FloatingPortal root={rootRef}>
      <FloatingFocusManager
        context={context}
        returnFocus={false}
        modal={false}
        order={['reference', 'content']}
      >
        <StyledDropdown
          ref={refs.setFloating}
          style={{ top: context.y || 0, left: context.x || 0, position: context.strategy }}
          {...getFloatingProps()}
        >
          <StyledActionBar>
            <SearchInput
              placeholder={t('settings.automation.accountingRules.form.searchContacts')}
              autoFocus={true}
              onSearch={value => setSearch(value)}
              onKeyDown={onKeyDown}
            />
            {onCreateContact && (
              <Button
                type="button"
                isSecondary={true}
                icon={<FaPlus />}
                onClick={() => {
                  onCreateContact()
                }}
              ></Button>
            )}
          </StyledActionBar>

          <StyledScrollableList ref={scrollRef}>
            <AnimatedContentLoader
              isLoading={isLoading}
              isEmpty={contacts.length === 0}
              isEmptyDescription={t('settings.automation.accountingRules.form.noContacts')}
            >
              {renderItems((contact, style, index) => (
                <StyledDropdownItem
                  key={contact.id}
                  style={{ height: `${ITEM_HEIGHT}px`, ...style }}
                  className={`${focusIndex === index ? 'keyboard-focused' : ''} ${
                    focusIndex === index && isEditButtonFocused ? 'edit-button-focused' : ''
                  }`}
                  onClick={() => handleContactSelect(contact)}
                  onKeyDown={onKeyDown}
                >
                  <StyledItemText>{contact.name}</StyledItemText>
                  <StyledRightItemsWrapper>
                    {onEditContact && (
                      <StyledEditButtonWrapper
                        className={isEditButtonFocused && focusIndex === index ? 'focused' : ''}
                      >
                        <Button
                          type="button"
                          isSecondary={true}
                          icon={<FaPen />}
                          onClick={e => {
                            e.stopPropagation()
                            onEditContact(contact)
                          }}
                        ></Button>
                      </StyledEditButtonWrapper>
                    )}
                    {selectedIds.includes(contact.id) && (
                      <StyledItemColumn>
                        <StyledCheckMarkWrapper>
                          <FaCheck />
                        </StyledCheckMarkWrapper>
                      </StyledItemColumn>
                    )}
                  </StyledRightItemsWrapper>
                </StyledDropdownItem>
              ))}
            </AnimatedContentLoader>
          </StyledScrollableList>
        </StyledDropdown>
      </FloatingFocusManager>
    </FloatingPortal>
  )
}

export default ContactsDropdown

const StyledDropdown = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  z-index: 9999;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.2);
`

const StyledActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
`

const StyledScrollableList = styled.div`
  overflow-y: auto;
  flex: 1;
`

const StyledEditButtonWrapper = styled.div`
  display: none;
  align-self: center;

  &.focused {
    display: block;
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  }
`

const StyledDropdownItem = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.4rem 0.6rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  cursor: pointer;

  &.keyboard-focused ${StyledEditButtonWrapper}, &:hover ${StyledEditButtonWrapper} {
    display: block;
  }
  &.keyboard-focused,
  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }

  &.edit-button-focused ${StyledEditButtonWrapper} {
    display: block;
  }
`

const StyledItemText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledCheckMarkWrapper = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  margin-left: ${({ theme }) => theme.spacing.sm}rem;

  svg {
    fill: ${({ theme }) => theme.colors.success};
  }
`

const StyledRightItemsWrapper = styled.div`
  display: flex;
  margin-left: auto;
`

const StyledItemColumn = styled.div`
  align-self: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
